import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, Container, useTheme, alpha } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import SectionWrapper from './SectionWrapper';
import { useHero } from '../context/HeroContext';
import { getHeroPoster } from '../shared/media/cardMedia';

/**
 * HERO SECTION - GENESIS CYCLE 5: "THE TACTICAL COVER"
 * Concept: Magazine Aesthetic + Intelligent Overlay (HUD).
 * No Violation Mode: Standard Scroll, Visible CTA.
 */
		const HeroSection = () => {
		    const theme = useTheme();
		    const { heroData } = useHero();
    const rafRef = useRef(null);
    const targetRef = useRef({ x: 0, y: 0 });
    const currentRef = useRef({ x: 0, y: 0 });
    const videoRef = useRef(null);
    const [bgOffset, setBgOffset] = useState({ x: 0, y: 0 });
	    const [videoReady, setVideoReady] = useState(false);

	    const deepBlack = '#050505';
	    const primaryRed = theme.palette.primary.main;
	    const manifestoText = (heroData?.title && String(heroData.title).trim())
	        ? String(heroData.title).trim()
	        : 'Não é sobre a tecnologia. É sobre o que você faz com ela.';
	    const heroVideoPlaybackRate = 0.85; // slightly slower (subtle)
		    const tickerHeight = 40;
		    const safeBottom = `calc(${tickerHeight}px + env(safe-area-inset-bottom, 0px))`;

		    const mediaItems = useMemo(
		        () => (Array.isArray(heroData?.media) ? heroData.media : []),
		        [heroData?.media]
		    );

	    // HERO: always video. No image fallback, no carousel, no rotation.
	    // While loading or if misconfigured: render deep black only.
		    const activeVideoUrl = useMemo(() => {
		        for (let i = mediaItems.length - 1; i >= 0; i--) {
		            const it = mediaItems[i];
		            if (it?.type === 'video' && it?.url) return it.url;
		        }
		        return null;
		    }, [mediaItems]);

	    const activeUrl = activeVideoUrl;

	    const bgTransform = useMemo(() => {
	        // Minimal parallax: keep it subtle (slow drift following mouse), no looping animation.
	        // Keep the bottom-right mark (R5 + Inteligencia Digital) inside the crop.
        return `translate3d(${bgOffset.x}px, ${bgOffset.y}px, 0) scale(1.08)`;
    }, [bgOffset.x, bgOffset.y]);

	    useEffect(() => {
	        // Reset readiness when the active hero media changes.
	        setVideoReady(false);
	    }, [activeUrl]);

    useEffect(() => {
        // Smoothly approach target offsets (slow/organic response).
        const tick = () => {
            const t = targetRef.current;
            const c = currentRef.current;
            const lerp = 0.06; // smaller = slower
            c.x += (t.x - c.x) * lerp;
            c.y += (t.y - c.y) * lerp;

            // Avoid excessive re-renders when nearly settled.
            const nx = Math.abs(c.x) < 0.01 ? 0 : c.x;
            const ny = Math.abs(c.y) < 0.01 ? 0 : c.y;
            setBgOffset((prev) => {
                if (Math.abs(prev.x - nx) < 0.02 && Math.abs(prev.y - ny) < 0.02) return prev;
                return { x: nx, y: ny };
            });

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <SectionWrapper
            id="hero" // Added ID for navigation
            sx={{
                bgcolor: deepBlack,
                color: 'white',
                // Keep content clear of the bottom ticker on short viewports.
                pb: safeBottom,
                // Scroll snap handled by Wrapper
            }}
            onMouseMove={(e) => {
                // Subtle response to mouse movement (interaction-driven, not a looping animation).
                const rect = e.currentTarget.getBoundingClientRect();
                const rx = (e.clientX - rect.left) / rect.width - 0.5;
                const ry = (e.clientY - rect.top) / rect.height - 0.5;
                // Keep it very small so we never crop the bottom-right logo/text out.
                const maxX = 6;
                const maxY = 5;
                targetRef.current = { x: -rx * maxX, y: -ry * maxY };
            }}
            onMouseLeave={() => {
                targetRef.current = { x: 0, y: 0 };
            }}
	        >
	            {/* 1. BACKGROUND IMAGE (The Magazine Base) */}
	            {activeUrl ? (
	                <Box
	                    sx={{
	                        position: 'absolute',
	                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 0,
                        transform: bgTransform,
                        willChange: 'transform',
                        transformOrigin: '85% 75%',
                        transition: 'transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)',
                        backgroundColor: deepBlack,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        // Avoid controlling playback via JS. Keep it resilient.
                        src={activeUrl}
                        poster={getHeroPoster(activeUrl)}
                        ref={videoRef}
                        onLoadedMetadata={() => {
                            if (videoRef.current) {
                                // Keep it subtle: too slow makes the hero feel laggy.
                                videoRef.current.playbackRate = heroVideoPlaybackRate;
                            }
                        }}
                        onLoadedData={() => setVideoReady(true)}
                        onCanPlay={() => setVideoReady(true)}
                        onPlay={() => {
                            if (videoRef.current) {
                                videoRef.current.playbackRate = heroVideoPlaybackRate;
                            }
                        }}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            // Frame towards bottom-right so "Inteligencia Digital" never gets cropped.
                            objectPosition: {
                                xs: '70% 62%',
                                sm: '72% 64%',
                                md: '76% 66%',
                                lg: '78% 68%',
                                xl: '80% 70%',
                            },
                            // Make the hero video black & white.
                            filter: 'grayscale(1) contrast(1.06) brightness(0.95)',
                            // Mirror horizontally so the subject swaps sides.
                            transform: 'scaleX(-1)',
                            transformOrigin: 'center',
                            backgroundColor: deepBlack,
                            // Prevent "flash" of a stale frame while the video buffers.
                            opacity: videoReady || getHeroPoster(activeUrl) ? 1 : 0,
                            transition: 'opacity 500ms ease',
                        }}
	                    />
	                </Box>
	            ) : (
	                <Box
	                    sx={{
	                        position: 'absolute',
	                        top: 0, left: 0, right: 0, bottom: 0,
	                        background: deepBlack,
	                        zIndex: 0,
	                        transform: bgTransform,
	                        willChange: 'transform',
	                        transformOrigin: '85% 75%',
	                        transition: 'transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)',
	                    }}
	                />
	            )}

            {/* 2. OVERLAY MASK (Readability, but keep the hero colors intact) */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    // Keep readability, but do NOT wash out the brand mark in the photo.
                    background:
                        // bias darkening to the left/bottom (where the big editorial text lives),
                        // keep the bottom-right (logo area in the photo) cleaner.
                        'linear-gradient(180deg, rgba(5,5,5,0.06) 0%, rgba(5,5,5,0.14) 55%, rgba(5,5,5,0.24) 100%),' +
                        'linear-gradient(90deg, rgba(5,5,5,0.30) 0%, rgba(5,5,5,0.10) 55%, rgba(5,5,5,0.00) 100%)',
                    zIndex: 1,
                    pointerEvents: 'none',
                }}
            />

            {/* 3. CONTENT (Editorial Layer) */}
            {/* WRAPPED IN CENTERING BOX FOR SECTION COMPLIANCE */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    // Extra spacing so the CTA never sits behind the ticker.
                    pb: { xs: 2, sm: 3 },
                }}
            >
                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ maxWidth: '1200px' }}>

                        {/* Massive Background Typography */}
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '15vw', md: '12vw' },
                                fontWeight: 900,
                                lineHeight: 0.8,
                                letterSpacing: '-0.07em',
                                wordSpacing: '-0.2em',
                                position: 'relative',
                                mb: 4
                            }}
                        >
                            {/* R5 in Brand Red (Solid) */}
                            <Box component="span" sx={{ color: primaryRed, mr: 2 }}>
                                R5
                            </Box>

                            {/* INTELIGENCIA in White Overlay */}
                            <Box component="span" sx={{ color: 'white', opacity: 0.9, mixBlendMode: 'overlay' }}>
                                INTEL
                                <br />
                                IGENCIA
                            </Box>
                        </Typography>

                        {/* Manifesto / Subtitle */}
                        <Box sx={{
                            borderLeft: `2px solid ${primaryRed}`,
                            pl: { xs: 2.5, sm: 4 },
                            // Responsive horizontal offset: avoid huge left shift on small screens.
                            ml: { xs: 0, sm: 6, md: 10 },
                            // Responsive vertical rhythm: avoid pushing content out of viewport on short screens.
                            mt: { xs: 4, sm: 6, md: 8, lg: 10 },
                            maxWidth: { xs: '92vw', sm: '600px' }
	                        }}>
			                            <Typography
			                                variant="subtitle1"
			                                sx={{
			                                    color: alpha('#FFFFFF', 0.82),
			                                    mb: 4,
			                                    maxWidth: '48ch',
			                                    fontWeight: 600,
			                                    // Keep the manifesto readable and visually balanced against the hero typography.
			                                    fontSize: {
			                                        xs: 'clamp(1.35rem, 3.0vmin, 1.7rem)',
			                                        md: 'clamp(1.65rem, 1.8vmin, 2.05rem)',
			                                    },
			                                    lineHeight: 1.45,
			                                    letterSpacing: '-0.01em',
			                                    textShadow: '0 1px 18px rgba(0,0,0,0.55)',
			                                }}
			                            >
			                                {manifestoText}
		                            </Typography>

                            <Button
                                component={RouterLink}
                                to="/visao-geral"
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '1rem',
                                    borderColor: 'white',
                                    color: '#0A0A0A',
                                    bgcolor: '#FFFFFF',
                                    borderRadius: '14px',
                                    px: 4,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    maxWidth: '100%',
                                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    '&:hover': {
                                        transform: 'scale(1.04)',
                                        bgcolor: primaryRed,
                                        color: '#FFFFFF',
                                        borderColor: primaryRed,
                                        boxShadow: '0 0 20px rgba(204, 0, 0, 0.35)'
                                    }
                                }}
                            >
                                Saiba Mais
                            </Button>
                        </Box>

                    </Box>
                </Container>
            </Box>

            {/* Bottom Data Ticker */}
            <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: `${tickerHeight}px`,
                borderTop: `1px solid ${alpha('#FFF', 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 4,
                fontFamily: 'monospace',
                fontSize: '11px',
                color: 'grey.500',
                zIndex: 2,
                bgcolor: alpha(deepBlack, 0.8),
                backdropFilter: 'blur(5px)'
            }}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{`COORDS: 00:00:00`}</Box>
                <Box sx={{ letterSpacing: '0.2em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    R5 PROJETOS // SYSTEM v1.0.355
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{`UPTIME: 99.9%`}</Box>
            </Box>

        </SectionWrapper>
    );
};

export default HeroSection;
